<template>
  <div class="container-fluid">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="mb-0">Bereich-Kontoverlauf</h3>
      <div class="d-flex gap-2 align-items-center">
        <!-- Filter Select -->
        <select 
          v-model="activeFilter" 
          @change="onFilterChange"
          class="form-select form-select-sm quitly-filter-auto-width"
        >
          <option v-for="filter in filters" :key="filter.key" :value="filter.key">
            {{ filter.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Current Balance Card -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-primary">{{ currentBalance }}</h5>
            <p class="card-text small text-muted">Aktueller Kontostand</p>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-success">{{ totalReceived }}</h5>
            <p class="card-text small text-muted">Quittungen erhalten</p>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-danger">{{ totalConsumed }}</h5>
            <p class="card-text small text-muted">Quittungen verbraucht</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="activity.length === 0" class="text-center py-5">
      <div class="text-muted">
        <div class="h1 mb-3">
          <font-awesome-icon icon="chart-line" class="text-secondary" />
        </div>
        <div class="h5 mb-2">Keine Aktivität gefunden</div>
        <div class="small">
          {{ activeFilter === 'all' ? 'Noch keine Transaktionen' : 'Keine Transaktionen in dieser Kategorie' }}
        </div>
      </div>
    </div>

    <!-- Activity List -->
    <div v-else class="card">
      <div class="card-header">
        <h6 class="mb-0">
          <font-awesome-icon icon="history" class="me-2" />
          Transaktionsverlauf
        </h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Datum</th>
                <th>Typ</th>
                <th>Benutzer</th>
                <th>Beschreibung</th>
                <th class="text-end">Betrag</th>
                <th class="text-end">Saldo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="activity in activity" :key="`${activity.type}-${activity.id}`">
                <td class="text-muted small">
                  {{ formatDate(activity.timestamp) }}
                </td>
                <td>
                  <span class="badge" :class="getActivityBadgeClass(activity.type)">
                    <font-awesome-icon :icon="getActivityIcon(activity.type)" class="me-1" />
                    {{ getActivityTypeLabel(activity.type) }}
                  </span>
                </td>
                <td>
                  <div v-if="activity.user">
                    <div class="fw-bold">{{ activity.user.firstName }} {{ activity.user.lastName }}</div>
                    <small class="text-muted">{{ activity.user.email ?? "Nutzer gelöscht" }}</small>
                  </div>
                  <span v-else class="text-muted">System</span>
                </td>
                                 <td>
                   <div>
                     <span v-if="activity.type === 'consumption' && activity.receiptId">
                       Quittung erstellt: 
                       <router-link 
                         :to="{ name: 'receipt-detail', params: { id: activity.receiptId } }"
                         class="fw-bold text-decoration-none"
                       >
                         {{ activity.receiptTitle }}
                         <font-awesome-icon icon="external-link-alt" class="ms-1 small" />
                       </router-link>
                     </span>
                     <span v-else>{{ activity.description }}</span>
                   </div>
                 </td>
                <td class="text-end">
                  <span :class="activity.amount > 0 ? 'text-success fw-bold' : 'text-danger fw-bold'">
                    {{ activity.amount > 0 ? '+' : '' }}{{ activity.amount }}
                    {{ Math.abs(activity.amount) === 1 ? 'Quittung' : 'Quittungen' }}
                  </span>
                </td>
                <td class="text-end">
                  <span class="fw-bold">{{ activity.balance }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <nav v-if="pagination && pagination.totalPages > 1" class="mt-4">
      <div class="d-flex justify-content-center align-items-center gap-3">
        <button
          @click="() => previousPage()"
          :disabled="pagination.page <= 1 || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          ← Vorherige
        </button>
        
        <span class="text-muted small">
          Seite {{ pagination.page }} von {{ pagination.totalPages }}
        </span>
        
        <button
          @click="() => nextPage()"
          :disabled="pagination.page >= pagination.totalPages || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          Nächste →
        </button>
      </div>
    </nav>
  </div>
</template>

<script>
import { coinTransferApi } from '../../api/coinTransfer'

export default {
  name: 'CoinConsumptionHistory',
  props: {
    teamId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      activity: [],
      loading: false,
      activeFilter: 'all',
      pagination: null,
      currentBalance: 0,
      totalReceived: 0,
      totalConsumed: 0,
      filters: [
        { key: 'all', label: 'Alle Transaktionen' },
        { key: 'transfer_in', label: 'Quittungen erhalten' },
        { key: 'purchase', label: 'Team-Upgrades' },
        { key: 'transfer_out', label: 'Quittungen übertragen' },
        { key: 'consumption', label: 'Quittungen verbraucht' }
      ]
    }
  },
  computed: {
  },
  watch: {
    teamId() {
      this.loadActivity()
    }
  },
  mounted() {
    this.loadActivity()
  },
  methods: {
    async loadActivity(page = 1) {
      this.loading = true
      
      try {
        const response = await coinTransferApi.getTeamAccountActivity(this.teamId, page, 20, this.activeFilter)
        this.activity = response.activity
        this.pagination = response.pagination
        this.currentBalance = response.currentBalance
        this.totalReceived = response.totalReceived
        this.totalConsumed = response.totalConsumed
      } catch (error) {
        console.error('Fehler beim Laden der Team-Aktivität:', error)
        this.activity = []
        this.pagination = null
        this.currentBalance = 0
        this.totalReceived = 0
        this.totalConsumed = 0
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.loadActivity(1) 
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getActivityIcon(type) {
      switch (type) {
        case 'transfer_in':
          return 'arrow-down'
        case 'transfer_out':
          return 'arrow-up'
        case 'purchase':
          return 'shopping-cart'
        case 'consumption':
          return 'receipt'
        default:
          return 'circle'
      }
    },

    getActivityBadgeClass(type) {
      switch (type) {
        case 'transfer_in':
          return 'bg-success'
        case 'transfer_out':
          return 'bg-warning text-dark'
        case 'purchase':
          return 'bg-info'
        case 'consumption':
          return 'bg-danger'
        default:
          return 'bg-secondary'
      }
    },

    getActivityTypeLabel(type) {
      switch (type) {
        case 'transfer_in':
          return 'Erhalten'
        case 'transfer_out':
          return 'Übertragen'
        case 'purchase':
          return 'Team-Upgrade'
        case 'consumption':
          return 'Verbraucht'
        default:
          return 'Unbekannt'
      }
    },

    previousPage() {
      if (this.pagination && this.pagination.page > 1) {
        this.loadActivity(this.pagination.page - 1)
      }
    },

    nextPage() {
      if (this.pagination && this.pagination.page < this.pagination.totalPages) {
        this.loadActivity(this.pagination.page + 1)
      }
    }
  }
}
</script>
